// GLOTECH Photo Engine — WebGL non-destructive renderer (same pipeline for preview and HD export).
// Global tonal/colour corrections only: no geometry warping, no skin smoothing, no generative AI → faces keep their identity.

export const TONAL_KEYS = ["exposure", "contrast", "highlights", "shadows", "whites", "blacks", "temperature", "tint", "saturation", "vibrance", "sharpness"];

const VERT = `attribute vec2 p; varying vec2 v; void main(){ v=(p+1.0)*0.5; gl_Position=vec4(p,0.0,1.0); }`;

const FRAG = `
precision highp float;
varying vec2 v;
uniform sampler2D t;
uniform mat3 m;
uniform vec2 texel;
uniform float exposure, contrast, highlights, shadows, whites, blacks, temp, tint, sat, vib, sharp;
float lum(vec3 c){ return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
void main(){
  vec2 s = (m * vec3(v.x, 1.0 - v.y, 1.0)).xy;
  vec3 c = texture2D(t, s).rgb;
  if (sharp > 0.0) {
    vec3 b = (texture2D(t, s + vec2(texel.x, 0.0)).rgb + texture2D(t, s - vec2(texel.x, 0.0)).rgb
            + texture2D(t, s + vec2(0.0, texel.y)).rgb + texture2D(t, s - vec2(0.0, texel.y)).rgb) * 0.25;
    c = clamp(c + (c - b) * sharp * 1.2, 0.0, 1.0);
  }
  vec3 lin = pow(c, vec3(2.2));
  lin *= vec3(1.0 + temp * 0.22, 1.0 - tint * 0.12, 1.0 - temp * 0.22);
  lin *= pow(2.0, exposure * 2.0);
  c = pow(max(lin, 0.0), vec3(1.0 / 2.2));
  float bp = -blacks * 0.08;
  float wp = 1.0 - whites * 0.15;
  c = (c - bp) / (wp - bp);
  float L = lum(c);
  float sw = 1.0 - smoothstep(0.0, 0.55, L);
  float hw = smoothstep(0.45, 1.0, L);
  c += shadows * 0.28 * sw + highlights * 0.28 * hw;
  c = (c - 0.5) * (1.0 + contrast * 0.5) + 0.5;
  c = clamp(c, 0.0, 1.0);
  c = mix(vec3(lum(c)), c, 1.0 + sat);
  float mx = max(c.r, max(c.g, c.b));
  float mn = min(c.r, min(c.g, c.b));
  float skin = step(c.b, c.g) * step(c.g, c.r);
  float amt = vib * (1.0 - (mx - mn)) * (1.0 - 0.6 * skin);
  c = mix(vec3(lum(c)), c, 1.0 + amt);
  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };

// Frame size after 90° rotations
export function frameSize(w, h, adj = {}) {
  return (adj.rotate || 0) % 180 ? [h, w] : [w, h];
}

// Affine map from output (crop space, top-left origin) to source texture coords
function geometryMatrix(adj, ignoreCrop) {
  const c = ignoreCrop ? FULL_CROP : adj.crop || FULL_CROP;
  let M = [[c.w, 0, c.x], [0, c.h, c.y], [0, 0, 1]];
  const mul = (A, B) => A.map((r) => [0, 1, 2].map((j) => r[0] * B[0][j] + r[1] * B[1][j] + r[2] * B[2][j]));
  if (adj.flip_h) M = mul([[-1, 0, 1], [0, 1, 0], [0, 0, 1]], M);
  if (adj.flip_v) M = mul([[1, 0, 0], [0, -1, 1], [0, 0, 1]], M);
  const r = ((adj.rotate || 0) % 360 + 360) % 360;
  const R = { 0: [[1, 0, 0], [0, 1, 0]], 90: [[0, 1, 0], [-1, 0, 1]], 180: [[-1, 0, 1], [0, -1, 1]], 270: [[0, -1, 1], [1, 0, 0]] }[r];
  M = mul([...R, [0, 0, 1]], M);
  // column-major for WebGL
  return new Float32Array([M[0][0], M[1][0], 0, M[0][1], M[1][1], 0, M[0][2], M[1][2], 1]);
}

export function createEngine(canvas) {
  const gl = canvas.getContext("webgl", { preserveDrawingBuffer: true, premultipliedAlpha: false });
  if (!gl) throw new Error("WebGL indisponible sur ce navigateur");
  const sh = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };
  const prog = gl.createProgram();
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  [[gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR]]
    .forEach(([k, val]) => gl.texParameteri(gl.TEXTURE_2D, k, val));
  const U = (n) => gl.getUniformLocation(prog, n);
  const maxTex = Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE), gl.getParameter(gl.MAX_RENDERBUFFER_SIZE), ...gl.getParameter(gl.MAX_VIEWPORT_DIMS));
  let size = [1, 1];

  return {
    maxTex,
    setImage(source) {
      size = [source.width, source.height];
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
    },
    render(adj = {}, { maxSize = Infinity, ignoreCrop = false } = {}) {
      const [fw, fh] = frameSize(size[0], size[1], adj);
      const c = ignoreCrop ? FULL_CROP : adj.crop || FULL_CROP;
      const cw = fw * c.w, ch = fh * c.h;
      const k = Math.min(1, maxSize / Math.max(cw, ch), maxTex / Math.max(cw, ch));
      const ow = Math.max(1, Math.round(cw * k)), oh = Math.max(1, Math.round(ch * k));
      canvas.width = ow;
      canvas.height = oh;
      gl.viewport(0, 0, ow, oh);
      gl.uniformMatrix3fv(U("m"), false, geometryMatrix(adj, ignoreCrop));
      const step = Math.max(1, 1 / k);
      gl.uniform2f(U("texel"), step / size[0], step / size[1]);
      const val = (key) => (adj[key] || 0) / 100;
      [["exposure", "exposure"], ["contrast", "contrast"], ["highlights", "highlights"], ["shadows", "shadows"], ["whites", "whites"],
        ["blacks", "blacks"], ["temp", "temperature"], ["tint", "tint"], ["sat", "saturation"], ["vib", "vibrance"], ["sharp", "sharpness"]]
        .forEach(([u, key]) => gl.uniform1f(U(u), val(key)));
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return { width: ow, height: oh, limited: k < 1 && maxSize === Infinity };
    },
    destroy() {
      gl.deleteTexture(tex);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    },
  };
}

export async function fetchBitmap(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Chargement impossible (${res.status})`);
  return createImageBitmap(await res.blob(), { imageOrientation: "from-image" });
}

const toBlob = (canvas, type, q) => new Promise((ok, ko) => canvas.toBlob((b) => (b ? ok(b) : ko(new Error("Encodage impossible"))), type, q));

export async function resizeTo(source, max, type, quality) {
  const k = Math.min(1, max / Math.max(source.width, source.height));
  const c = document.createElement("canvas");
  c.width = Math.round(source.width * k);
  c.height = Math.round(source.height * k);
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, c.width, c.height);
  return toBlob(c, type, quality);
}

// Browser-side variants for upload (keeps the GLOTECH server light)
export async function makeUploadVariants(file) {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const [thumb, preview] = await Promise.all([resizeTo(bmp, 480, "image/webp", 0.75), resizeTo(bmp, 2048, "image/jpeg", 0.85)]);
    return { thumb, preview, width: bmp.width, height: bmp.height };
  } finally {
    bmp.close?.();
  }
}

// HD export: renders the ORIGINAL at full resolution → JPEG q=0.95 + web/thumbnail versions
export async function exportHD(engine, canvas, originalUrl, adj) {
  const bmp = await fetchBitmap(originalUrl);
  try {
    engine.setImage(bmp);
    const info = engine.render(adj);
    const [full, web, thumb] = await Promise.all([
      toBlob(canvas, "image/jpeg", 0.95),
      resizeTo(canvas, 2048, "image/jpeg", 0.88),
      resizeTo(canvas, 480, "image/webp", 0.75),
    ]);
    return { full, web, thumb, ...info };
  } finally {
    bmp.close?.();
  }
}

// Simple, conservative auto-correction (grey-world white balance + mid-tone exposure)
export function autoAdjust(source) {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, 64, 64);
  const d = ctx.getImageData(0, 0, 64, 64).data;
  let r = 0, g = 0, b = 0, lo = 0, hi = 0;
  const n = d.length / 4;
  for (let i = 0; i < d.length; i += 4) {
    r += d[i]; g += d[i + 1]; b += d[i + 2];
    const L = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
    if (L < 20) lo++;
    if (L > 240) hi++;
  }
  r /= n * 255; g /= n * 255; b /= n * 255;
  const mean = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const clamp = (x, a) => Math.round(Math.max(-a, Math.min(a, x)));
  const ev = Math.log2(Math.pow(0.46, 2.2) / Math.max(1e-3, Math.pow(mean, 2.2)));
  return {
    exposure: clamp((ev / 2) * 100 * 0.8, 50),
    temperature: clamp(((b - r) / Math.max(1e-3, r + b)) * 220, 35),
    tint: clamp(((g - (r + b) / 2) / Math.max(1e-3, g)) * 150, 25),
    shadows: lo / n > 0.05 ? 20 : 0,
    highlights: hi / n > 0.03 ? -25 : 0,
    vibrance: 8,
  };
}
